import { useCallback, useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  accionesPendientes,
  DE_HERRAMIENTA,
  enviarPendientes,
  type AccionEnCola,
} from '@/lib/campo/colaDeAcciones'
import { subirPosicionesPendientes } from '@/lib/campo/colaDePosiciones'
import { subirLoQueEspera } from '@/lib/campo/colaDeOrdenes'

import { sincronizarFotos } from './useColaDeFotos'

/**
 * MANDAR LOS MARCAJES QUE ESPERABAN SEÑAL.
 *
 * Mismo motor que la cola de fotos y por la misma razón: vive a nivel de
 * módulo para que la tanda siga aunque la pantalla se cierre, y un candado
 * evita dos envíos a la vez del mismo marcaje —que crearía dos llegadas—.
 *
 * Se intenta al volver la conexión y al abrir la aplicación. No se insiste en
 * bucle: sin red, el navegador avisa cuando vuelve, y gastar batería
 * preguntando cada minuto en una obra sin cobertura es justo lo que no se
 * quiere.
 */
let corriendo = false
const oyentes = new Set<() => void>()

function avisarCambio() {
  oyentes.forEach((fn) => fn())
}

export async function sincronizarAcciones(): Promise<void> {
  if (corriendo) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  corriendo = true
  try {
    await enviarPendientes()
    // Y lo demás que esperaba señal: las órdenes levantadas en la calle y
    // los renglones cotizados sin cobertura. En este orden, porque un
    // borrador cuelga de su orden.
    await subirLoQueEspera()
    // Las fotos DESPUÉS de las órdenes: las de una orden levantada sin señal
    // no tienen a qué colgarse hasta que su orden existe. Si suben primero
    // fallan y esperaban al siguiente corte de red para reintentarse.
    await sincronizarFotos()

    /*
     * Y los cruces de geocerca que el teléfono vio sin poder contarlos.
     *
     * Van LOS ÚLTIMOS a propósito: los marcajes que el técnico hizo a mano
     * mandan sobre los que dedujo el GPS. Si pulsó «Terminé» a las 11:00 y
     * además el rastreo lo vio salir, lo que vale es su gesto; que entre
     * primero evita que la salida automática llegue antes y la orden se
     * cierre dos veces.
     */
    try {
      await subirPosicionesPendientes()
    } catch {
      /* se quedan guardadas: tienen su hora y aguantan tres días */
    }

    // Se avisa SIEMPRE, no solo cuando algo sale: si una marca quedó
    // rechazada, la pantalla tiene que enterarse igual —o más—, porque eso
    // es lo que el técnico necesita ver.
    avisarCambio()
  } finally {
    corriendo = false
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void sincronizarAcciones())

  /*
   * LA SEÑAL VUELVE SIN AVISAR (0324).
   *
   * `online` solo se dispara si el teléfono alcanzó a creerse sin red. Con la
   * raya que no transmite nunca se lo cree, así que el evento no llega y lo
   * pendiente se quedaba esperando a que el técnico abriera otra pantalla o
   * marcara algo nuevo (revisión de la app de Techos, 27 oct). Ahora también
   * se intenta al volver a la aplicación y, mientras quede algo, cada 45 s.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void sincronizarAcciones()
  })
  window.setInterval(() => {
    void accionesPendientes().then((p) => {
      if (p.some((a) => !a.rechazada)) void sincronizarAcciones()
    })
  }, 45_000)
}

/**
 * Lo que queda pendiente, para poder decirlo en la pantalla.
 *
 * Un marcaje esperando no es un error, pero el técnico tiene derecho a saber
 * que su «llegué» todavía no ha salido del teléfono: si cree que ya está y el
 * teléfono se pierde, nadie lo sabría.
 */
/**
 * ¿Hay una llegada de esta orden esperando señal?
 *
 * La tarjeta lo necesita para saber que el técnico ya está en el sitio
 * aunque el servidor todavía no se haya enterado.
 */
export function useLlegadaEnCola(ordenTrabajoId: string): boolean {
  const [hay, setHay] = useState(false)

  useEffect(() => {
    let vivo = true

    const mirar = async () => {
      const pendientes = await accionesPendientes()
      if (!vivo) return
      setHay(
        pendientes.some(
          (a) => a.clase === 'llegada' && a.ordenTrabajoId === ordenTrabajoId && !a.rechazada,
        ),
      )
    }

    void mirar()
    oyentes.add(mirar)
    window.addEventListener('mega:cola-de-acciones', mirar)

    return () => {
      vivo = false
      oyentes.delete(mirar)
      window.removeEventListener('mega:cola-de-acciones', mirar)
    }
  }, [ordenTrabajoId])

  return hay
}

export function useColaDeAcciones() {
  const [pendientes, setPendientes] = useState<AccionEnCola[]>([])
  const clienteConsultas = useQueryClient()

  const refrescar = useCallback(async () => {
    setPendientes(await accionesPendientes())
  }, [])

  useEffect(() => {
    void refrescar()

    const avisar = () => {
      void refrescar()
      // Lo que subió cambia el estado de la orden: la pantalla tiene que
      // enterarse sola, sin que el técnico recargue.
      void clienteConsultas.invalidateQueries({ queryKey: ['mi-trabajo'] })
      void clienteConsultas.invalidateQueries({ queryKey: ['visitas'] })
      // Y lo de la herramienta: una devolución o una recogida que acaba de
      // salir cambia lo que lleva encima.
      void clienteConsultas.invalidateQueries({ queryKey: ['mis-herramientas'] })
      void clienteConsultas.invalidateQueries({ queryKey: ['mis-recogidas'] })
      // Y la visita registrada sin señal (0256).
      void clienteConsultas.invalidateQueries({ queryKey: ['mis-ordenes-por-cotizar'] })
      void clienteConsultas.invalidateQueries({ queryKey: ['mi-programacion'] })
    }

    oyentes.add(avisar)
    // Y cuando algo se guarda sin señal, para que el aviso salga enseguida.
    window.addEventListener('mega:cola-de-acciones', avisar)
    void sincronizarAcciones()

    return () => {
      oyentes.delete(avisar)
      window.removeEventListener('mega:cola-de-acciones', avisar)
    }
  }, [refrescar, clienteConsultas])

  return { pendientes, reintentar: () => void sincronizarAcciones() }
}

/**
 * Las marcas de esta orden que esperan señal, con su hora.
 *
 * La tarjeta las necesita para saber en qué está el técnico aunque el
 * servidor no se haya enterado: si salió a almorzar sin señal, está
 * almorzando; si después marcó la vuelta, ya volvió. Manda la más reciente.
 */
export function useMarcasEnCola(
  ordenTrabajoId: string,
): { clase: AccionEnCola['clase']; ocurridoEn: string }[] {
  const [marcas, setMarcas] = useState<{ clase: AccionEnCola['clase']; ocurridoEn: string }[]>([])

  useEffect(() => {
    let vivo = true

    const mirar = async () => {
      const pendientes = await accionesPendientes()
      if (!vivo) return
      setMarcas(
        pendientes
          .filter((a) => a.ordenTrabajoId === ordenTrabajoId && !a.rechazada)
          .map((a) => ({ clase: a.clase, ocurridoEn: a.ocurridoEn })),
      )
    }

    void mirar()
    oyentes.add(mirar)
    window.addEventListener('mega:cola-de-acciones', mirar)

    return () => {
      vivo = false
      oyentes.delete(mirar)
      window.removeEventListener('mega:cola-de-acciones', mirar)
    }
  }, [ordenTrabajoId])

  return marcas
}

/**
 * Lo de herramienta que espera señal: peticiones de recogida, de traslado,
 * devoluciones y cancelaciones. La llave de cada una (préstamo, recogida o
 * código del QR) va en `ordenTrabajoId`.
 */
export function useHerramientaEnCola(): AccionEnCola[] {
  const [enCola, setEnCola] = useState<AccionEnCola[]>([])

  useEffect(() => {
    let vivo = true

    const mirar = async () => {
      const pendientes = await accionesPendientes()
      if (!vivo) return
      setEnCola(pendientes.filter((a) => DE_HERRAMIENTA.includes(a.clase) && !a.rechazada))
    }

    void mirar()
    oyentes.add(mirar)
    window.addEventListener('mega:cola-de-acciones', mirar)

    return () => {
      vivo = false
      oyentes.delete(mirar)
      window.removeEventListener('mega:cola-de-acciones', mirar)
    }
  }, [])

  return enCola
}

/**
 * Las visitas registradas sin señal (0256): la orden de servicio y lo que
 * contestó el cliente. La tarjeta las necesita para no volver a pedir el
 * registro mientras el teléfono no lo haya podido mandar.
 */
export function useVisitasEnCola(): { ordenServicioId: string; quiereCotizar: boolean }[] {
  const [enCola, setEnCola] = useState<{ ordenServicioId: string; quiereCotizar: boolean }[]>([])

  useEffect(() => {
    let vivo = true

    const mirar = async () => {
      const pendientes = await accionesPendientes()
      if (!vivo) return
      setEnCola(
        pendientes
          .filter((a) => a.clase === 'visita' && !a.rechazada)
          .map((a) => ({
            ordenServicioId: a.ordenTrabajoId,
            quiereCotizar: Boolean(a.argumentos.p_quiere_cotizar),
          })),
      )
    }

    void mirar()
    oyentes.add(mirar)
    window.addEventListener('mega:cola-de-acciones', mirar)

    return () => {
      vivo = false
      oyentes.delete(mirar)
      window.removeEventListener('mega:cola-de-acciones', mirar)
    }
  }, [])

  return enCola
}
